import React, { useContext } from 'react';
import { Box, Button, ButtonProps, Flex, IconButton, Text, VStack } from '@chakra-ui/react';
import { FiChevronLeft, FiX } from 'react-icons/fi';
import type { Campaign, Client, Collab } from '@foodfluence/shared';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { getClientIdFromCollabRequest } from 'utils/collabUtils';
import { ff } from '../../../../theme';

/** The brand behind an invite, for sheet copy ("Note to Cafe Co"). */
export const useBrand = (collab: Collab, campaign: Campaign): { client: Client | undefined; name: string } => {
  const clients = useContext(ClientDataContext);
  const client = collab && campaign ? clients[getClientIdFromCollabRequest(collab, campaign)] : undefined;
  return { client, name: client?.accountName || 'the brand' };
};

/** Header for the counter / decline bottom sheets (board ReviewInvite sheet): grabber, step bar, title, close. */
export const SheetHeader: React.FC<{
  title: string;
  onClose: () => void;
  step?: { index: number; total: number; name: string };
  onBack?: () => void;
  sub?: React.ReactNode;
}> = ({ title, onClose, step, onBack, sub }) => (
  <VStack align="stretch" spacing="16px" mt="-10px" mb="16px">
    <Box alignSelf="center" w="40px" h="4px" borderRadius="full" bg={ff.border} aria-hidden />
    {step && (
      <Flex align="center" gap="8px">
        {onBack && (
          <IconButton
            aria-label="Previous step"
            icon={<FiChevronLeft size={22} strokeWidth={2.4} />}
            onClick={onBack}
            flex="none"
            w="44px"
            h="44px"
            minW="44px"
            ml="-10px"
            borderRadius="full"
            bg="transparent"
            color={ff.ink}
            _hover={{ bg: ff.grey }}
          />
        )}
        <VStack flex={1} align="stretch" spacing="6px">
          <Text fontSize="12px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#55555A" lineHeight="normal">
            Step {step.index} of {step.total} · {step.name}
          </Text>
          <Flex gap="4px" aria-hidden>
            {Array.from({ length: step.total }, (_, i) => (
              <Box key={i} flex={1} h="4px" borderRadius="full" bg={i < step.index ? ff.ink : ff.card} />
            ))}
          </Flex>
        </VStack>
      </Flex>
    )}
    <Flex align="flex-start" justify="space-between" gap="12px">
      <Text as="h2" m={0} pt="6px" textStyle="display" fontSize="27px" lineHeight={1.12} letterSpacing="0">
        {title}
      </Text>
      <IconButton
        aria-label="Close"
        icon={<FiX size={18} strokeWidth={2.4} />}
        onClick={onClose}
        flex="none"
        w="44px"
        h="44px"
        minW="44px"
        mr="-10px"
        borderRadius="full"
        bg="transparent"
        color={ff.ink}
        _hover={{ bg: ff.grey }}
      />
    </Flex>
    {sub && (
      <Text mt="-6px" fontSize="15px" lineHeight={1.45} color={ff.body}>
        {sub}
      </Text>
    )}
  </VStack>
);

/** Radio list of reasons (board sheet radios). */
export const ReasonRadios: React.FC<{
  label: string;
  options: string[];
  value: string | null;
  onChange: (option: string) => void;
}> = ({ label, options, value, onChange }) => (
  <VStack role="radiogroup" aria-label={label} align="stretch" spacing="8px">
    {options.map(option => {
      const on = value === option;
      return (
        <Button
          key={option}
          role="radio"
          aria-checked={on}
          onClick={() => onChange(option)}
          justifyContent="flex-start"
          h="auto"
          minH="52px"
          px="16px"
          gap="12px"
          borderRadius="14px"
          border={on ? `2px solid ${ff.ink}` : `1px solid ${ff.border}`}
          bg={on ? ff.limeSurface : 'white'}
          color={ff.ink}
          fontSize="15px"
          fontWeight={600}
          lineHeight="normal"
          textAlign="left"
          whiteSpace="normal"
          _hover={{ bg: on ? ff.limeSurface : 'white' }}
          _active={{ bg: ff.limeSurface }}
        >
          <Box
            as="span"
            flex="none"
            w="20px"
            h="20px"
            boxSizing="border-box"
            borderRadius="full"
            border={on ? `6px solid ${ff.ink}` : '2px solid #8A8A8E'}
            bg={on ? ff.lime : 'transparent'}
          />
          {option}
        </Button>
      );
    })}
  </VStack>
);

const PILL: ButtonProps = { flex: 1, h: '52px', borderRadius: 'full', fontSize: '15px', fontWeight: 600, lineHeight: 'normal' };

export type SheetButtonTone = 'lime' | 'red' | 'redOutline' | 'outline';

const TONES: Record<SheetButtonTone, ButtonProps> = {
  lime: { bg: ff.lime, color: ff.ink, _hover: { bg: ff.lime } },
  red: { bg: '#B42318', color: 'white', _hover: { bg: '#B42318' } },
  redOutline: { bg: 'white', color: '#B42318', border: '1.5px solid #B42318', _hover: { bg: 'white' } },
  outline: { bg: 'white', color: ff.ink, border: `1.5px solid ${ff.ink}`, _hover: { bg: 'white' } },
};

/** Pill button for sheet footers; disabled renders the board's grey "off" style. */
export const SheetButton: React.FC<ButtonProps & { tone?: SheetButtonTone }> = ({ tone = 'lime', ...props }) => (
  <Button
    {...PILL}
    {...TONES[tone]}
    _disabled={{ bg: ff.line, color: '#6E6E72', border: 0, cursor: 'not-allowed', opacity: 1 }}
    {...props}
  />
);

export const SheetFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Flex gap="10px" mt="16px">
    {children}
  </Flex>
);
